import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Activity, AlertTriangle, Clock, Crosshair, Layers, Loader, MapPin, RefreshCw, ScrollText, Server, Users,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SectionHeader } from '../components/ui/SectionHeader';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { LogbookActor } from '../components/logbook/LogbookActor';
import { LogbookActionBadge } from '../components/logbook/LogbookBadges';
import { LogbookBreakdown } from '../components/logbook/LogbookBreakdown';
import { LogbookCompleteness } from '../components/logbook/LogbookCompleteness';
import { LogbookEventList } from '../components/logbook/LogbookEventList';
import { LogbookServerChip } from '../components/logbook/LogbookServerChip';
import { LogbookViewBar } from '../components/logbook/LogbookViewBar';
import { LogbookNearbyMap } from '../components/logbook/LogbookNearbyMap';
import { AddToCaseModal } from '../components/logbook/AddToCaseModal';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLogbookServers } from '../hooks/useLogbookServers';
import { logbookApiService, LogbookApiError } from '../services/logbookApi';
import { canProtectLogbook } from '../utils/logbookPermissions';
import { LOGBOOK_WINDOWS, dimensionLabel, formatCount, isEvidenceGap, relativeTime } from '../utils/logbook';
import { filtersToParams, resolveWindow, toAggregationQuery, toSearchQuery } from '../utils/logbookFilters';
import { DEFAULT_LOGBOOK_VIEW, groupEvents, partitionEvents, type LogbookViewOptions } from '../utils/logbookView';
import {
  DEFAULT_NEARBY_QUERY, NEARBY_RADII, formatSpan, nearbyFromParams, nearbyToParams, peopleIn, rankPresence,
  toNearbyFilters, type LogbookNearbyQuery, type LogbookPresence,
} from '../utils/logbookNearby';
import { parseCoordinates } from '../utils/coords';
import { formatLocalDateTime } from '../utils/timestamps';
import { cn } from '../utils/cn';
import type { LogbookAggregationResult, LogbookEvent, LogbookSearchResult } from '../types/logbook';

/**
 * Who was here.
 *
 * The question a griefed base actually raises is not "what happened" but "who was standing here".
 * This page takes a server, a position, a radius and a window, and answers with the people whose
 * evidence fell inside that space in that time, ranked by how much of it there is, with when each
 * of them was first and last seen there.
 *
 * Presence is counted from the aggregation, which covers every matching record, and never from the
 * page of records underneath, which is a sample of at most two hundred. The sample only adds what a
 * count cannot: what each person did and how close they came. Position samples count toward presence
 * even though the list hides them by default, because standing somewhere is the whole question.
 */

const DIMENSIONS = ['minecraft:overworld', 'minecraft:the_nether', 'minecraft:the_end'];

interface Draft {
  serverId: string;
  coordinates: string;
  radius: number;
  dimension: string;
  windowId: string;
  after?: number;
  before?: number;
}

const EMPTY_DRAFT: Draft = { serverId: '', coordinates: '', ...DEFAULT_NEARBY_QUERY };

function draftFrom(query: LogbookNearbyQuery): Draft {
  return {
    serverId: query.serverId,
    coordinates: `${query.x} ${query.y} ${query.z}`,
    radius: query.radius,
    dimension: query.dimension,
    windowId: query.windowId,
    after: query.after,
    before: query.before,
  };
}

function toInputValue(ms?: number): string {
  if (!ms) return '';
  const date = new Date(ms);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromInputValue(value: string): number | undefined {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : undefined;
}

/** The window as the tail of a sentence: "in the last 24 hours", "between 3 Sep 2026 14:00 and ...". */
function describeWindow(query: LogbookNearbyQuery): string {
  if (query.windowId === 'custom' && query.after != null && query.before != null) {
    return `between ${formatLocalDateTime(new Date(query.after))} and ${formatLocalDateTime(new Date(query.before))}`;
  }
  const preset = LOGBOOK_WINDOWS.find((entry) => entry.id === query.windowId);
  if (!preset) return 'in the selected window';
  return preset.ms === null ? 'across all retained history' : `in the ${preset.label.toLowerCase()}`;
}

export function LogbookNearbyView() {
  useDocumentTitle('Who was here');
  const navigate = useNavigate();
  const { identity } = useAuth();
  const canProtect = canProtectLogbook(identity);
  const [params, setParams] = useSearchParams();
  const { servers, byId: serversById, loading: serversLoading, error: serversError } = useLogbookServers();

  // The URL is the question. Submitting the form writes it; everything else reads it.
  const paramsKey = params.toString();
  const query = useMemo(() => nearbyFromParams(new URLSearchParams(paramsKey)), [paramsKey]);

  const [draft, setDraft] = useState<Draft>(() => (query ? draftFrom(query) : EMPTY_DRAFT));
  const [formError, setFormError] = useState<string | null>(null);
  const [byActor, setByActor] = useState<LogbookAggregationResult | null>(null);
  const [byAction, setByAction] = useState<LogbookAggregationResult | null>(null);
  const [page, setPage] = useState<LogbookSearchResult | null>(null);
  const [view, setView] = useState<LogbookViewOptions>(DEFAULT_LOGBOOK_VIEW);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LogbookApiError | Error | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [held, setHeld] = useState<Set<string>>(new Set());
  const [attaching, setAttaching] = useState<LogbookEvent[] | null>(null);
  const [caseNote, setCaseNote] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (query) setDraft(draftFrom(query));
  }, [query]);

  useEffect(() => {
    if (!query) return;
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPageError(null);

    const filters = toNearbyFilters(query);
    const now = Date.now();
    const run = async () => {
      const [actors, actions, records] = await Promise.allSettled([
        logbookApiService.aggregate(toAggregationQuery(filters, 'actor_id', now), controller.signal),
        logbookApiService.aggregate(toAggregationQuery(filters, 'action', now), controller.signal),
        logbookApiService.search(toSearchQuery(filters, now), controller.signal),
      ]);
      if (cancelled || controller.signal.aborted) return;

      setByActor(actors.status === 'fulfilled' ? actors.value : null);
      setByAction(actions.status === 'fulfilled' ? actions.value : null);
      setPage(records.status === 'fulfilled' ? records.value : null);

      // The ranking survives either source failing, so only losing both is an error.
      if (actors.status === 'rejected' && records.status === 'rejected') {
        const reason = actors.reason instanceof LogbookApiError ? actors.reason : records.reason;
        setError(reason instanceof Error ? reason : new Error('That question could not be answered.'));
      } else if (records.status === 'rejected') {
        setPageError(records.reason instanceof Error ? records.reason.message : 'The records could not be loaded.');
      } else if (actors.status === 'rejected') {
        setPageError('The presence count could not be loaded, so the ranking below comes from the loaded records only.');
      }
      setLoading(false);
    };

    void run();
    return () => { cancelled = true; controller.abort(); };
  }, [query, nonce]);

  const submit = useCallback((event: FormEvent) => {
    event.preventDefault();
    const parsed = parseCoordinates(draft.coordinates);
    if (!draft.serverId) { setFormError('Choose the server the position is on.'); return; }
    if (!parsed) { setFormError('Coordinates need three numbers, in the order x, y, z.'); return; }
    if (draft.windowId === 'custom' && !(draft.after != null && draft.before != null && draft.after < draft.before)) {
      setFormError('A custom window needs a start that comes before its end.');
      return;
    }
    setFormError(null);
    setParams(nearbyToParams({
      serverId: draft.serverId,
      x: parsed.x, y: parsed.y, z: parsed.z,
      radius: draft.radius,
      dimension: draft.dimension,
      windowId: draft.windowId,
      after: draft.windowId === 'custom' ? draft.after : undefined,
      before: draft.windowId === 'custom' ? draft.before : undefined,
    }));
  }, [draft, setParams]);

  const recentre = useCallback((event: LogbookEvent) => {
    if (!query) return;
    setParams(nearbyToParams({
      ...query,
      serverId: event.server_id,
      dimension: event.dimension,
      x: event.x, y: event.y, z: event.z,
    }));
  }, [query, setParams]);

  const filters = useMemo(() => (query ? toNearbyFilters(query) : null), [query]);
  const logbookHref = useMemo(() => (filters ? `/logbook?${filtersToParams(filters).toString()}` : '/logbook'), [filters]);
  const actorHref = useCallback((actorId: string) => {
    if (!filters) return '/logbook';
    return `/logbook?${filtersToParams({ ...filters, actorId, actorName: '' }).toString()}`;
  }, [filters]);

  const events = page?.events ?? [];
  const presence = useMemo(
    () => (query ? rankPresence(byActor, events, query) : []),
    [query, byActor, events],
  );
  const people = useMemo(() => peopleIn(presence), [presence]);
  const unattributed = presence.find((entry) => entry.actorId === '');

  const totals = useMemo(() => {
    const source = byAction ?? byActor;
    return {
      records: (source?.groups ?? []).reduce((sum, group) => sum + group.event_count, 0),
      actions: byAction?.groups.length ?? 0,
      partial: byActor?.state === 'partial',
      gaps: events.filter(isEvidenceGap).length,
    };
  }, [byAction, byActor, events]);

  const partition = useMemo(() => partitionEvents(events, view, held), [events, view, held]);
  const runs = useMemo(() => groupEvents(partition.visible, view.groupWindowMs), [partition.visible, view.groupWindowMs]);

  const oldestReceipt = useMemo(() => {
    const receipts = (page?.scope ?? [])
      .map((serverId) => serversById.get(serverId)?.lastReceivedAt)
      .filter((value): value is number => typeof value === 'number');
    return receipts.length > 0 ? Math.min(...receipts) : null;
  }, [page, serversById]);

  const windowSpan = useMemo(() => (filters ? resolveWindow(filters).spanMs : null), [filters]);
  const notConfigured = error instanceof LogbookApiError && error.isNotConfigured;
  const tooBroad = error instanceof LogbookApiError && error.isTooBroad;
  const server = query ? serversById.get(query.serverId) : undefined;

  const patch = (changes: Partial<Draft>) => setDraft((current) => ({ ...current, ...changes }));

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title="Who was here"
        description="Who was recorded near a position, and when."
        icon={MapPin}
        backTo="/logbook"
        backLabel="Back to Logbook"
        meta={query && server ? <LogbookServerChip serverId={query.serverId} server={server} detailed /> : undefined}
        actions={query ? (
          <>
            <Link to={logbookHref} className="glass-button">
              <ScrollText className="w-4 h-4" />
              Open in Logbook
            </Link>
            <button type="button" className="btn-primary" onClick={() => setNonce((value) => value + 1)} disabled={loading}>
              <RefreshCw className={loading ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
              Ask again
            </button>
          </>
        ) : undefined}
      />

      {/* The question */}
      <form onSubmit={submit} className="glass-card p-4 md:p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.2fr_1.4fr_1fr_1fr] gap-3">
          <label className="block min-w-0">
            <span className="block text-xs font-medium text-glass-400 mb-1.5">Server</span>
            <div className="relative">
              <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
              <select
                value={draft.serverId}
                onChange={(changed) => patch({ serverId: changed.target.value })}
                className="glass-select pl-9 w-full"
                disabled={serversLoading && servers.length === 0}
                aria-label="Server"
              >
                <option value="">{serversLoading && servers.length === 0 ? 'Loading servers' : 'Choose a server'}</option>
                {servers.map((entry) => (
                  <option key={entry.serverId} value={entry.serverId}>
                    {entry.pack ? `${entry.pack.title} (${entry.serverId})` : entry.serverId}
                  </option>
                ))}
                {draft.serverId && !servers.some((entry) => entry.serverId === draft.serverId) && (
                  <option value={draft.serverId}>{draft.serverId}</option>
                )}
              </select>
            </div>
          </label>

          <label className="block min-w-0">
            <span className="block text-xs font-medium text-glass-400 mb-1.5">Position</span>
            <div className="relative">
              <Crosshair className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
              <input
                type="text"
                value={draft.coordinates}
                onChange={(changed) => patch({ coordinates: changed.target.value })}
                placeholder="x y z"
                className="form-input pl-9 font-mono text-xs"
                aria-label="Coordinates"
                autoFocus={!query}
              />
            </div>
          </label>

          <label className="block min-w-0">
            <span className="block text-xs font-medium text-glass-400 mb-1.5">Dimension</span>
            <select
              value={draft.dimension}
              onChange={(changed) => patch({ dimension: changed.target.value })}
              className="glass-select w-full"
              aria-label="Dimension"
            >
              {DIMENSIONS.map((dimension) => <option key={dimension} value={dimension}>{dimensionLabel(dimension)}</option>)}
              {!DIMENSIONS.includes(draft.dimension) && <option value={draft.dimension}>{dimensionLabel(draft.dimension)}</option>}
            </select>
          </label>

          <label className="block min-w-0">
            <span className="block text-xs font-medium text-glass-400 mb-1.5">Window</span>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
              <select
                value={draft.windowId}
                onChange={(changed) => patch({ windowId: changed.target.value })}
                className="glass-select pl-9 w-full"
                aria-label="Time window"
              >
                {LOGBOOK_WINDOWS.map((window) => <option key={window.id} value={window.id}>{window.label}</option>)}
                <option value="custom">Between two times</option>
              </select>
            </div>
          </label>
        </div>

        {draft.windowId === 'custom' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">From</span>
              <input
                type="datetime-local"
                value={toInputValue(draft.after)}
                onChange={(changed) => patch({ after: fromInputValue(changed.target.value) })}
                className="form-input w-full"
                aria-label="Window start"
              />
            </label>
            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">To</span>
              <input
                type="datetime-local"
                value={toInputValue(draft.before)}
                onChange={(changed) => patch({ before: fromInputValue(changed.target.value) })}
                className="form-input w-full"
                aria-label="Window end"
              />
            </label>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs font-medium text-glass-400 shrink-0">Within</span>
            <div className="segmented" role="radiogroup" aria-label="Radius">
              {NEARBY_RADII.map((radius) => (
                <button
                  key={radius}
                  type="button"
                  role="radio"
                  aria-checked={draft.radius === radius}
                  className="segmented-item segmented-item-sm tabular-nums"
                  data-active={draft.radius === radius}
                  onClick={() => patch({ radius })}
                >
                  {radius}
                </button>
              ))}
            </div>
            <span className="text-xs text-glass-500 shrink-0">blocks</span>
          </div>
          <div className="flex-1" />
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            Who was here
          </button>
        </div>

        {formError && (
          <div className="alert alert-warning">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{formError}</span>
          </div>
        )}
      </form>

      {serversError && !notConfigured && (
        <div className="alert alert-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>The server list could not be loaded, so servers show as raw ids. {serversError}</span>
        </div>
      )}

      {caseNote && (
        <div className="alert alert-info">
          <ScrollText className="w-4 h-4 shrink-0" />
          <span className="flex-1">{caseNote}</span>
          <Link to="/logbook/cases" className="link text-xs shrink-0">Open cases</Link>
        </div>
      )}

      {!query ? (
        <EmptyState
          icon={MapPin}
          title="Name a place to start"
          description="Choose a server, enter coordinates, and set a radius and window."
        />
      ) : notConfigured ? (
        <EmptyState icon={ScrollText} title="Logbook is not connected yet" description={error?.message} />
      ) : error ? (
        <EmptyState
          tone="danger"
          icon={AlertTriangle}
          title={tooBroad ? 'That question was too broad' : 'That question could not be answered'}
          description={tooBroad ? `${error.message} Try a shorter window or a smaller radius.` : error.message}
          action={(
            <button type="button" className="glass-button" onClick={() => setNonce((value) => value + 1)}>
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          )}
        />
      ) : (
        <>
          {pageError && (
            <div className="alert alert-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{pageError}</span>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile
              label="People seen"
              value={formatCount(people.length)}
              icon={Users}
              tone="accent"
              loading={loading}
              hint={totals.partial ? 'At least this many' : `Within ${query.radius} blocks ${describeWindow(query)}`}
            />
            <StatTile
              label="Records in the area"
              value={formatCount(totals.records)}
              icon={Activity}
              tone="info"
              loading={loading}
            />
            <StatTile label="Distinct actions" value={formatCount(totals.actions)} icon={Layers} tone="purple" loading={loading} />
            <StatTile
              label="Unattributed"
              value={formatCount(unattributed?.count ?? 0)}
              icon={AlertTriangle}
              tone={unattributed ? 'warning' : 'neutral'}
              loading={loading}
            />
          </div>

          <div className="glass-card p-4 md:p-5 space-y-4">
            <SectionHeader
              title="Presence"
              icon={Users}
              count={loading ? undefined : presence.length}
              description={(
                <>
                  Within {query.radius} blocks of{' '}
                  <span className="font-mono tabular-nums text-glass-300">{query.x}, {query.y}, {query.z}</span>{' '}
                  in {dimensionLabel(query.dimension)} {describeWindow(query)}, ranked by records left.
                </>
              )}
            />

            {loading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {Array.from({ length: 4 }, (_, index) => <SkeletonLoader key={index} className="h-32 w-full rounded-xl" />)}
              </div>
            ) : presence.length === 0 ? (
              <EmptyState
                bare
                compact
                icon={MapPin}
                title="Nobody was recorded here"
                description="Widen the radius or the window."
              />
            ) : (
              <ol className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {presence.map((entry, index) => (
                  <PresenceCard
                    key={entry.actorId || 'unattributed'}
                    rank={index + 1}
                    entry={entry}
                    windowSpan={windowSpan}
                    href={actorHref(entry.actorId)}
                  />
                ))}
              </ol>
            )}

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <LogbookBreakdown
              title="What happened here"
              icon={Layers}
              aggregation={byAction}
              loading={loading}
              onSelect={(_, value) => {
                if (!filters) return;
                navigate(`/logbook?${filtersToParams({ ...filters, actions: [value] }).toString()}`);
              }}
            />
            <div className="glass-card p-4 md:p-5">
              <SectionHeader title="Where they were" icon={Crosshair} />
              {loading ? (
                <SkeletonLoader className="aspect-square w-full max-w-[420px] mx-auto rounded-lg" />
              ) : (
                <LogbookNearbyMap events={events} presence={presence} centre={query} radius={query.radius} />
              )}
            </div>
          </div>

          <div className="glass-card overflow-hidden">
            <div className="p-4 md:p-5 pb-3 space-y-3">
              <SectionHeader title="Records" icon={ScrollText} count={loading ? undefined : partition.visible.length} />
              <LogbookCompleteness
                completeness={page?.completeness}
                scope={page?.scope}
                snapshot={page?.snapshot}
                gapCount={totals.gaps}
                oldestReceiptAt={oldestReceipt}
              />
              <LogbookViewBar value={view} onChange={setView} partition={partition} runCount={runs.length} />
            </div>

            {!loading && runs.length === 0 ? (
              <div className="px-4 pb-6">
                <EmptyState
                  bare
                  icon={ScrollText}
                  title={partition.hiddenTotal > 0 ? 'Everything loaded is hidden' : 'No records to show'}
                  description={partition.hiddenTotal > 0
                    ? `${formatCount(partition.hiddenTotal)} records are hidden by the view settings above.`
                    : 'Nothing was captured in that space in that window.'}
                />
              </div>
            ) : (
              <LogbookEventList
                runs={runs}
                serversById={serversById}
                loading={loading}
                canProtect={canProtect}
                heldEventIds={held}
                hideServer
                onAddToCase={setAttaching}
                onFilterActor={(event) => navigate(actorHref(event.actor_id))}
                onFilterAction={(event) => {
                  if (!filters) return;
                  navigate(`/logbook?${filtersToParams({ ...filters, actions: [event.action] }).toString()}`);
                }}
                onFilterNearby={recentre}
              />
            )}

            {page?.cursor && (
              <div className="p-4 border-t border-glass-200/10 flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs text-glass-500">
                  {formatCount(events.length)} newest records loaded. Open in Logbook for the rest.
                </span>
                <Link to={logbookHref} className="glass-button-sm">
                  <ScrollText className="w-4 h-4" />
                  Open in Logbook
                </Link>
              </div>
            )}
          </div>
        </>
      )}

      {attaching && (
        <AddToCaseModal
          events={attaching}
          onClose={() => setAttaching(null)}
          onAttached={(result, target) => {
            setHeld((current) => {
              const next = new Set(current);
              for (const event of attaching) next.add(event.event_id);
              return next;
            });
            setCaseNote(`${result.attached} ${result.attached === 1 ? 'record' : 'records'} copied into ${target.reference}`
              + (result.duplicates > 0 ? `, ${result.duplicates} already there.` : '.'));
          }}
        />
      )}
    </div>
  );
};

interface PresenceCardProps {
  rank: number;
  entry: LogbookPresence;
  windowSpan: number | null;
  href: string;
}

const MAX_ACTION_BADGES = 4;

/**
 * One actor's presence: who, how much, when, how close, and what they did.
 *
 * The count and the bounds are the facts; the actions and the distance are colour from the sample
 * and are labelled as such when the sample is thin, so a thin card reads as thin rather than as a
 * person who did nothing.
 */
const PresenceCard = ({ rank, entry, windowSpan, href }: PresenceCardProps) => {
  const span = entry.firstSeenAt != null && entry.lastSeenAt != null ? entry.lastSeenAt - entry.firstSeenAt : null;
  const named = entry.actorId !== '';
  const shownActions = entry.actions.slice(0, MAX_ACTION_BADGES);
  const moreActions = entry.actions.length - shownActions.length;

  return (
    <li className={cn('subcard p-3 md:p-4 flex gap-3', !named && 'border-dashed')}>
      <span
        className={cn('icon-tile icon-tile-sm tabular-nums text-xs font-semibold shrink-0', rank === 1 ? 'icon-tile-accent' : 'icon-tile-neutral')}
        aria-label={`Rank ${rank}`}
      >
        {rank}
      </span>

      <div className="flex-1 min-w-0 space-y-2.5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <LogbookActor actorId={entry.actorId} actorName={entry.actorName} actorKind={entry.actorKind} inline={false} className="min-w-0" />
          <span className="badge badge-accent tabular-nums shrink-0" title={entry.boundsExact ? 'Every matching record, from the aggregation' : 'From the loaded records only'}>
            {formatCount(entry.count)} {entry.count === 1 ? 'record' : 'records'}
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div className="min-w-0">
            <dt className="text-glass-500">First seen</dt>
            <dd className="text-glass-200 tabular-nums truncate" title={entry.firstSeenAt != null ? formatLocalDateTime(new Date(entry.firstSeenAt)) : undefined}>
              {entry.firstSeenAt != null ? relativeTime(entry.firstSeenAt) : 'Unknown'}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-glass-500">Last seen</dt>
            <dd className="text-glass-200 tabular-nums truncate" title={entry.lastSeenAt != null ? formatLocalDateTime(new Date(entry.lastSeenAt)) : undefined}>
              {entry.lastSeenAt != null ? relativeTime(entry.lastSeenAt) : 'Unknown'}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-glass-500">Present across</dt>
            <dd className="text-glass-200 tabular-nums truncate">
              {span == null ? 'Unknown' : formatSpan(span)}
              {span != null && windowSpan != null && span >= windowSpan * 0.95 && (
                <span className="text-glass-500"> (the whole window)</span>
              )}
            </dd>
          </div>
          <div className="min-w-0">
            <dt className="text-glass-500">Came within</dt>
            <dd className="text-glass-200 tabular-nums truncate">
              {entry.nearestDistance == null
                ? 'Not in the loaded records'
                : entry.nearestDistance < 1 ? 'the block itself' : `${entry.nearestDistance} blocks`}
            </dd>
          </div>
        </dl>

        {shownActions.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {shownActions.map((action) => (
              <span key={action.action} className="inline-flex items-center gap-1">
                <LogbookActionBadge action={action.action} />
                <span className="text-[11px] tabular-nums text-glass-500">{formatCount(action.count)}</span>
              </span>
            ))}
            {moreActions > 0 && <span className="text-xs text-glass-500">+{moreActions} more</span>}
          </div>
        ) : (
          <p className="text-xs text-glass-500 italic">None of their records are in the loaded page.</p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-glass-200/10">
          <span className="text-[11px] text-glass-500">
            {entry.boundsExact ? 'Exact bounds' : 'Sampled bounds'}
            {entry.sampled > 0 && entry.sampled < entry.count && ` (${formatCount(entry.sampled)} of ${formatCount(entry.count)} loaded)`}
          </span>
          <Link to={href} className="link text-xs">
            {named ? 'Their records here' : 'These records'}
          </Link>
        </div>
      </div>
    </li>
  );
};
